import { gtag } from 'ga-gtag';

import { MEASUREMENT_ID } from 'Constants/Constants';
import { env } from 'Constants/Env';
import type { Enum } from 'types/enum-types';

const idpUrls = {
  int: 'https://idpassets-ping-int.uefa.com/idp.js',
  pre: 'https://idpassets-ping-pre.uefa.com/idp.js',
  prod: 'https://idpassets-ping.uefa.com/idp.js',
};

// Max time to wait for the GA client/session ids before initializing the IDP without them.
const GA_IDS_TIMEOUT_MS = 1000;

export const UserEvents = {
  LOGIN: 'login',
  LOGOUT: 'logout',
  LOGIN_SILENT: 'loginSilent',
  FAN_LOADED: 'fanLoaded',
  FAN_UPDATED: 'fanUpdated',
  TOKEN_EXPIRED: 'tokenExpired',
  UNAUTHORIZED: 'UnAuthorized',
  FAN_CENTER_CLOSED: 'fanCenterClosed',
  ERROR: 'error',
} as const;
export type UserEvents = Enum<typeof UserEvents>;

export const LoginSources = {
  HEADER: 'Header',
  CONTENT: 'Content Section',
} as const;
export type LoginSources = Enum<typeof LoginSources>;

export const IdpFanCenterRequestType = {
  NewTab: 'newtab',
  Popup: 'popup',
  Redirect: 'redirect',
};
export type IdpFanCenterRequestType = Enum<typeof IdpFanCenterRequestType>;

export const IdpStoreMode = {
  LocalStorage: 'localStorage',
  SessionStorage: 'sessionStorage',
} as const;
export type IdpStoreMode = Enum<typeof IdpStoreMode>;

export const IdpRequestType = {
  Redirect: 'redirect',
  Popup: 'popup',
  Silent: 'silent',
};
export type IdpRequestType = Enum<typeof IdpRequestType>;

export type IdpAuthSettings = {
  requestType?: IdpRequestType;
  prompt?: string;
  locale?: string;
  scope?: string;
  policyId?: string;
};

export type IdpWebProfileSettings = {
  width?: number;
  height?: number;
  requestType?: IdpFanCenterRequestType;
};

export interface IdpWebClientSettings {
  /** The client application's identifier as registered with the OIDC/OAuth2 */
  client_id: string;
  client_secret?: string;
  /** The type of response desired from the OIDC/OAuth2 provider (default: "code") */
  response_type?: string;
  /** The scope being requested from the OIDC/OAuth2 provider (default: "openid") */
  scope?: string;
  /** The return URI of the client application to redirect back after a successful response from the OIDC/OAuth2 provider.
   * if not provided, it will be the host location.origin
   */
  return_uri: string;
  /**optional
   * if not provided, it will be return_uri
   */
  popup_return_uri?: string;
  /**optional
   * if not provided, it will be return_uri
   */
  silent_return_uri?: string;
  /** The return URI of the client application to redirect back after a successful logout.
   * if not provided, it will be return_uri
   */
  logout_return_uri?: string;
  /** optional OIDC protocol param */
  prompt?: string;
  locale?: string;
  /** appropriate for specific flows */
  policyId?: string;
  /** The type of storage to store user data (default: "localStorage") */
  storeMode?: IdpStoreMode;
  // Specifies the maximum amount of time in seconds allowed since the user last authenticated. If the max_age value is exceeded, the user must re-authenticate.
  maxAge?: number;
  // this is a custom param specific for our IDP Web SDK and it Specifies the maximum amount of time in seconds for the token to expire.
  maxTokenAge?: number;
}

export type Fan = {
  sub: string;
  uefaId?: string;
  email?: string;
  photoURL?: string;
  uefa_country_code?: string;

  nickname?: string;
  given_name?: string;
  family_name?: string;

  /*Favourite men national team */
  national_team_men?: number[];
  /*Favourite women national team */
  national_team_women?: number[];
  /*Favourite men club team */
  club_men?: number[];
  /*Favourite women club team */
  club_women?: number[];
  /* list of favourite leagues */
  leagues?: number[];

  interested_in_teams?: number[];
  [key: string]: unknown;
};

export type IdpEventEmitter = {
  on(event: UserEvents, callback: (e?: any) => void): IdpEventEmitter;
  off(event: UserEvents, callback: (e?: any) => void): IdpEventEmitter;
  once(event: UserEvents, callback: (e?: any) => void): IdpEventEmitter;
  emit(event: UserEvents, ...args: unknown[]): IdpEventEmitter;
};

export type Result<T, E> = {
  success: boolean;
  value?: T;
  error?: E;
};

export interface IdpError {
  message: string;
  code?: string;
}

export type TokensObject = {
  id_token?: string;
  access_token?: string;
  expires_in?: number;
};

export interface IdpManager {
  changePassword(currentPassword: string, newPassword: string): Promise<Result<boolean, IdpError>>;
  getAccessToken(): Promise<string | undefined>;
  getEventEmitter(): IdpEventEmitter;
  getFan(): Promise<Result<Fan | null, IdpError>>;
  getInstance(settings: IdpWebClientSettings): Promise<IdpManager>;
  getTokens(): Promise<TokensObject | undefined>;
  initialize(settings: IdpWebClientSettings): void;
  isFanLoggedIn(): Promise<boolean>;
  isReady: boolean;
  login(args?: IdpAuthSettings): Promise<void>;
  loginSilent(): Promise<Result<boolean, IdpError>>;
  logout(redirect: boolean): Promise<void>;
  register(args?: IdpAuthSettings): Promise<void>;
  renewToken(): Promise<Result<boolean, IdpError>>;
  signinHostSitesCallback(): Promise<unknown>;
  showWebProfile(args?: IdpWebProfileSettings): Promise<Result<boolean, IdpError>>;
  storeUserCustom(user: unknown): Promise<Result<boolean, IdpError>>;
  updateFan(fanProfile: Fan): Promise<Result<boolean, IdpError | IdpError[]>>;
}
declare global {
  interface Window {
    IdpManager?: IdpManager;
  }
}

let idpPromise: Promise<IdpManager> = null;

class IdpClient {
  static getBasePath = (withTrailingSlash = true) => {
    if (window.location.hostname === 'localhost') {
      withTrailingSlash = false;
    }

    const basePath = `${window.location.origin}${window.location.pathname.substring(0, window.location.pathname.lastIndexOf('/'))}`;
    return withTrailingSlash
      ? `${basePath}${basePath.endsWith('/') ? '' : '/'}`
      : `${basePath.endsWith('/') ? basePath.substring(0, basePath.lastIndexOf('/')) : basePath}`;
  };

  static appendComponentScript() {
    return new Promise<void>((resolve) => {
      const url = idpUrls[env];
      let script = document.querySelector<HTMLScriptElement>(`script[src*="${url}"]`);

      if (script) return resolve();
      script = document.createElement('script');

      script.src = url;
      script.onload = () => resolve();
      script.onerror = () => resolve();
      document.head.appendChild(script);
    });
  }

  static waitIdp() {
    return new Promise<IdpManager>((resolve) => {
      if (window.IdpManager) {
        resolve(window.IdpManager);
        return;
      }

      const interval = setInterval(() => {
        if (window.IdpManager) {
          clearInterval(interval);
          resolve(window.IdpManager);
        }
      }, 1000);
    });
  }

  static init(locale) {
    idpPromise ??= (async () => {
      await this.appendComponentScript();
      const idp = await this.waitIdp();

      const idpKey = import.meta.env.VITE_IDP_API_KEY;

      const idpInitializeSettings = {
        client_id: idpKey,
        return_uri: this.getBasePath(),
        //logout_return_uri: this.getBasePath(),
        locale: locale,
        scope: 'openid address email offline_access idp_profile idp_favourites p1:update:user:apps',
        analyticsSettings: {
          ga_client_id: '',
          ga_session_id: '',
        },
      };

      const gaPromise = new Promise<{ clientId: string; sessionId: string | null }>(async (resolve, reject) => {
        if (!MEASUREMENT_ID || !gtag) {
          return reject('GA_MEASUREMENT_ID or gtag not set');
        }
        try {
          const getSessionId = () =>
            new Promise<string>((resolveSession, rejectSession) => {
              gtag('get', MEASUREMENT_ID, 'session_id', (session_id: string) => {
                if (!session_id) {
                  return rejectSession('session_id not found');
                }
                resolveSession(session_id);
              });
            });

          const getClientId = () =>
            new Promise<string>((resolveClient, rejectClient) => {
              gtag('get', MEASUREMENT_ID, 'client_id', (clientId: string) => {
                if (!clientId) {
                  return rejectClient('client_id not found');
                }
                resolveClient(clientId);
              });
            });

          const [sessionId, clientId] = await Promise.all([getSessionId(), getClientId()]);
          resolve({ clientId, sessionId });
        } catch (e) {
          reject(e);
        }
      });

      // When gtag.js is blocked (e.g. by an adblocker) its `get` callbacks never fire, so
      // `gaPromise` would stay pending forever and block `idp.initialize()` from ever running.
      // Bound the wait with a timeout so analytics stays best-effort and never blocks login.
      const gaTimeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject('GA ids lookup timed out (gtag blocked?)'), GA_IDS_TIMEOUT_MS),
      );

      try {
        const ids = await Promise.race([gaPromise, gaTimeout]);
        idpInitializeSettings.analyticsSettings.ga_client_id = ids.clientId;
        idpInitializeSettings.analyticsSettings.ga_session_id = ids.sessionId;
      } catch (e) {
        console.warn('Proceeding with IDP init without GA analytics ids:', e);
      }

      await idp.initialize(idpInitializeSettings);
      return idp;
    })();

    return idpPromise;
  }
}

export default IdpClient;
